import React, { useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import CustomerDashboard from "../components/CustomerDashboard";
import { api, humanBytes } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import {
    Users,
    FolderKanban,
    FileText,
    HardDrive,
    ArrowUpRight,
    Image,
    Video,
    FileType2,
    FileQuestion,
    Sparkles,
    DollarSign,
    Activity,
} from "lucide-react";

// USD → EUR estimate for display (rough). User can update via env if needed.
const USD_TO_EUR = 0.92;
const formatCost = (usd) => {
    const v = Number(usd) || 0;
    const eur = v * USD_TO_EUR;
    if (eur < 0.01) return `< €0,01`;
    if (eur < 1) return `€${eur.toFixed(3)}`;
    return `€${eur.toFixed(2)}`;
};
const formatCostExact = (usd) => `$${(Number(usd) || 0).toFixed(6)}`;

const KIND_ICONS = {
    image: Image,
    video: Video,
    pdf: FileType2,
    text: FileText,
    other: FileQuestion,
};

const KIND_LABEL = {
    image: "Afbeeldingen",
    video: "Video's",
    pdf: "PDF's",
    text: "Documenten",
    other: "Overige",
};

export default function DashboardPage() {
    const { user } = useAuth();
    const [stats, setStats] = useState(null);
    const [error, setError] = useState("");
    const [backfilling, setBackfilling] = useState(false);
    const [recovering, setRecovering] = useState(false);
    const isAdmin = user?.role === "admin";

    const loadStats = () => {
        api.get("/stats/overview")
            .then((r) => setStats(r.data))
            .catch(() => setError("Kon statistieken niet laden."));
    };

    useEffect(() => {
        if (!isAdmin) return;
        loadStats();
    }, [isAdmin]);

    const runBackfill = async () => {
        setBackfilling(true);
        try {
            const { data } = await api.post("/admin/backfill-thumbs");
            const msg = `${data.processed} thumbnails aangemaakt`
                + (data.failed ? `, ${data.failed} mislukt` : "")
                + (data.remaining ? ` — nog ${data.remaining} resterend, klik nogmaals.` : "");
            (await import("sonner")).toast.success(msg);
            loadStats();
        } catch (e) {
            (await import("sonner")).toast.error("Backfill mislukt — probeer opnieuw.");
        } finally {
            setBackfilling(false);
        }
    };

    const runRecover = async () => {
        setRecovering(true);
        try {
            const { data } = await api.post("/admin/recover-stranded-uploads");
            const parts = [];
            if (data.recovered) parts.push(`${data.recovered} hersteld`);
            if (data.deleted_orphans) parts.push(`${data.deleted_orphans} orphans verwijderd`);
            if (data.failed) parts.push(`${data.failed} mislukt`);
            const msg = parts.length
                ? `${data.scanned} gevonden — ${parts.join(", ")}.`
                : "Geen hangende uploads gevonden.";
            (await import("sonner")).toast.success(msg);
            loadStats();
        } catch (e) {
            (await import("sonner")).toast.error("Herstel mislukt — probeer opnieuw.");
        } finally {
            setRecovering(false);
        }
    };

    if (!isAdmin) {
        return (
            <AppShell>
                <CustomerDashboard />
            </AppShell>
        );
    }

    return (
        <AppShell>
            <div className="px-4 py-6 md:px-10 md:py-10 max-w-[1500px]" data-testid="dashboard-page">
                <header className="flex items-end justify-between gap-4 flex-wrap mb-8 md:mb-10">
                    <div>
                        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">
                            01 — Overzicht
                        </div>
                        <h1 className="font-display text-4xl md:text-5xl tracking-tighter font-black leading-none">
                            Dashboard
                        </h1>
                        <p className="text-zinc-500 mt-2 text-sm">
                            Het volledige overzicht van je portal.
                        </p>
                    </div>
                    <Link
                        to="/customers"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-[#0044FF] hover:bg-[#0033CC] text-white rounded-md text-sm font-medium transition-colors"
                        data-testid="dashboard-cta-customers"
                    >
                        Klanten beheren
                        <ArrowUpRight className="w-4 h-4" />
                    </Link>
                </header>

                {/* Backfill thumbnails utility */}
                <div className="bg-white border border-zinc-200 rounded-md p-4 mb-6 flex items-center justify-between gap-4 flex-wrap" data-testid="backfill-card">
                    <div className="min-w-0">
                        <div className="text-xs uppercase tracking-[0.18em] text-zinc-500 mb-1">
                            Onderhoud
                        </div>
                        <div className="text-sm font-medium">
                            Thumbnails opnieuw aanmaken voor oude foto's & video's
                        </div>
                        <p className="text-xs text-zinc-500 mt-1 max-w-xl">
                            Bestanden die geüpload werden vóór deze functie hadden geen
                            thumbnail. Klik op de knop om ze in batch aan te maken.
                        </p>
                    </div>
                    <button
                        onClick={runBackfill}
                        disabled={backfilling}
                        className="inline-flex items-center gap-2 px-4 py-2 border border-zinc-200 rounded-md text-sm font-medium hover:bg-zinc-50 disabled:opacity-50"
                        data-testid="backfill-thumbs-button"
                    >
                        {backfilling ? "Bezig…" : "Thumbnails aanmaken"}
                    </button>
                </div>

                {/* Recover stranded uploads */}
                <div className="bg-white border border-amber-200 rounded-md p-4 mb-6 flex items-center justify-between gap-4 flex-wrap" data-testid="recover-card">
                    <div className="min-w-0">
                        <div className="text-xs uppercase tracking-[0.18em] text-amber-700 mb-1">
                            Hangende uploads
                        </div>
                        <div className="text-sm font-medium">
                            Herstel bestanden die 0&nbsp;B tonen of geen AI-beschrijving kregen
                        </div>
                        <p className="text-xs text-zinc-500 mt-1 max-w-xl">
                            Als een upload onderbroken werd, blijven bestanden hangen op 0 B
                            zonder thumbnail of beschrijving. Deze knop scant je opslag,
                            herstelt afgewerkte uploads en verwijdert onvolledige.
                        </p>
                    </div>
                    <button
                        onClick={runRecover}
                        disabled={recovering}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-md text-sm font-medium disabled:opacity-50"
                        data-testid="recover-uploads-button"
                    >
                        {recovering ? "Bezig…" : "Hangende uploads herstellen"}
                    </button>
                </div>

                {error && (
                    <div className="text-red-600 bg-red-50 border border-red-200 rounded-md p-3 text-sm mb-6">
                        {error}
                    </div>
                )}

                {!stats ? (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        {[1, 2, 3, 4].map((i) => (
                            <div key={i} className="h-32 bg-white border border-zinc-200 rounded-md animate-pulse" />
                        ))}
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 mb-4 md:mb-6">
                            <Stat label="Klanten" value={stats.customers} icon={Users} testid="stat-customers" />
                            <Stat label="Projecten" value={stats.projects} icon={FolderKanban} testid="stat-projects" />
                            <Stat label="Bestanden" value={stats.files} icon={FileText} testid="stat-files" />
                            <Stat
                                label="Opslag"
                                value={humanBytes(stats.storage_used_bytes)}
                                icon={HardDrive}
                                testid="stat-storage"
                                sub={`van ${humanBytes(stats.storage_quota_bytes)} quotum`}
                            />
                        </div>

                        {/* AI cost row */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 mb-8 md:mb-10" data-testid="ai-cost-row">
                            <Stat
                                label="Totale AI-kost"
                                value={formatCost(stats.ai?.total_cost_usd || 0)}
                                icon={DollarSign}
                                testid="stat-ai-cost"
                                sub={formatCostExact(stats.ai?.total_cost_usd || 0)}
                                accent
                            />
                            <Stat
                                label="AI-aanvragen"
                                value={(stats.ai?.total_calls || 0).toLocaleString("nl-BE")}
                                icon={Sparkles}
                                testid="stat-ai-calls"
                                sub={`Model: ${stats.ai?.model || "—"}`}
                            />
                            <Stat
                                label="Input tokens"
                                value={Number(stats.ai?.total_input_tokens || 0).toLocaleString("nl-BE")}
                                icon={Activity}
                                testid="stat-ai-input-tokens"
                                sub={`$${stats.ai?.input_price_per_m_usd || 0}/M`}
                            />
                            <Stat
                                label="Output tokens"
                                value={Number(stats.ai?.total_output_tokens || 0).toLocaleString("nl-BE")}
                                icon={Activity}
                                testid="stat-ai-output-tokens"
                                sub={`$${stats.ai?.output_price_per_m_usd || 0}/M`}
                            />
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-md p-6" data-testid="storage-by-customer">
                                <div className="flex items-center justify-between mb-6">
                                    <h2 className="font-display text-2xl tracking-tight font-bold">
                                        Top klanten op opslag
                                    </h2>
                                    <Link to="/customers" className="text-sm text-[#0044FF] hover:underline">
                                        Alle klanten →
                                    </Link>
                                </div>
                                {stats.top_customers.length === 0 ? (
                                    <EmptyHint text="Nog geen klanten. Maak je eerste klant aan om te beginnen." link="/customers" linkLabel="Klant aanmaken" />
                                ) : (
                                    <div className="space-y-4">
                                        {stats.top_customers.map((c) => {
                                            const pct = c.storage_quota_bytes
                                                ? Math.min(100, (c.storage_used_bytes / c.storage_quota_bytes) * 100)
                                                : 0;
                                            return (
                                                <Link
                                                    to={`/customers/${c.id}`}
                                                    key={c.id}
                                                    className="block group"
                                                    data-testid={`top-customer-${c.id}`}
                                                >
                                                    <div className="flex items-center justify-between mb-1.5">
                                                        <div className="font-medium group-hover:text-[#0044FF]">{c.name}</div>
                                                        <div className="text-sm font-mono text-zinc-500">
                                                            {humanBytes(c.storage_used_bytes)} / {humanBytes(c.storage_quota_bytes)}
                                                        </div>
                                                    </div>
                                                    <div className="h-1.5 bg-zinc-200 rounded-full overflow-hidden">
                                                        <div
                                                            className={`h-full ${pct > 90 ? "bg-red-500" : pct > 75 ? "bg-amber-500" : "bg-[#0044FF]"}`}
                                                            style={{ width: `${pct}%` }}
                                                        />
                                                    </div>
                                                </Link>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            <div className="bg-white border border-zinc-200 rounded-md p-6" data-testid="files-by-kind">
                                <h2 className="font-display text-2xl tracking-tight font-bold mb-6">
                                    Per type
                                </h2>
                                {stats.by_kind.length === 0 ? (
                                    <div className="text-sm text-zinc-500">Nog geen bestanden.</div>
                                ) : (
                                    <ul className="space-y-3">
                                        {stats.by_kind.map((k) => {
                                            const Icon = KIND_ICONS[k.kind] || FileQuestion;
                                            return (
                                                <li key={k.kind} className="flex items-center justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-8 h-8 rounded-sm bg-zinc-100 border border-zinc-200 flex items-center justify-center">
                                                            <Icon className="w-4 h-4 text-zinc-700" />
                                                        </div>
                                                        <div>
                                                            <div className="text-sm font-medium">{KIND_LABEL[k.kind] || k.kind}</div>
                                                            <div className="text-xs font-mono text-zinc-500">{humanBytes(k.size)}</div>
                                                        </div>
                                                    </div>
                                                    <div className="text-2xl font-display font-bold tracking-tight">{k.count}</div>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </div>
                        </div>

                        {/* AI cost details: top customers + 30d trend */}
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mt-8 md:mt-10" data-testid="ai-cost-details">
                            <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-md p-6">
                                <div className="flex items-center justify-between mb-6">
                                    <div>
                                        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-1">
                                            AI-kost per klant
                                        </div>
                                        <h2 className="font-display text-2xl tracking-tight font-bold">
                                            Top klanten op AI-uitgaven
                                        </h2>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                                            Totaal
                                        </div>
                                        <div className="font-display text-2xl font-black tracking-tighter">
                                            {formatCost(stats.ai?.total_cost_usd || 0)}
                                        </div>
                                    </div>
                                </div>
                                {(!stats.top_customers_by_cost || stats.top_customers_by_cost.every((c) => !c.ai_cost_usd)) ? (
                                    <EmptyHint text="Nog geen AI-kosten geregistreerd. Upload bestanden om te starten." />
                                ) : (
                                    <div className="space-y-3">
                                        {stats.top_customers_by_cost
                                            .filter((c) => c.ai_cost_usd > 0 || c.ai_call_count > 0)
                                            .map((c) => {
                                                const total = stats.ai?.total_cost_usd || 1;
                                                const pct = total ? Math.min(100, ((c.ai_cost_usd || 0) / total) * 100) : 0;
                                                return (
                                                    <Link
                                                        to={`/customers/${c.id}`}
                                                        key={c.id}
                                                        className="block group"
                                                        data-testid={`top-cost-${c.id}`}
                                                    >
                                                        <div className="flex items-center justify-between mb-1.5">
                                                            <div className="font-medium group-hover:text-[#0044FF]">{c.name}</div>
                                                            <div className="text-sm font-mono text-zinc-700">
                                                                {formatCost(c.ai_cost_usd)}{" "}
                                                                <span className="text-zinc-400">
                                                                    · {c.ai_call_count} calls
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                                                            <div
                                                                className="h-full bg-[#0044FF]"
                                                                style={{ width: `${pct}%` }}
                                                            />
                                                        </div>
                                                    </Link>
                                                );
                                            })}
                                    </div>
                                )}
                            </div>

                            <div className="bg-white border border-zinc-200 rounded-md p-6" data-testid="ai-trend">
                                <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-1">
                                    Laatste 30 dagen
                                </div>
                                <h2 className="font-display text-2xl tracking-tight font-bold mb-4">
                                    Verbruik
                                </h2>
                                {(!stats.ai?.cost_trend_30d || stats.ai.cost_trend_30d.length === 0) ? (
                                    <div className="text-sm text-zinc-500">
                                        Geen activiteit in de laatste 30 dagen.
                                    </div>
                                ) : (
                                    <CostSparkline data={stats.ai.cost_trend_30d} />
                                )}
                                <div className="mt-4 pt-4 border-t border-zinc-100 text-xs font-mono text-zinc-500 space-y-1">
                                    <div className="flex justify-between">
                                        <span>Per foto / video frame:</span>
                                        <span>~{formatCost(0.000213)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>Per PDF (10 p.):</span>
                                        <span>~{formatCost(0.0025)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </AppShell>
    );
}

function Stat({ label, value, icon: Icon, testid, sub, accent }) {
    return (
        <div
            className={`rounded-md p-4 md:p-6 flex flex-col gap-2 md:gap-3 border ${
                accent
                    ? "bg-[#0044FF] text-white border-[#0044FF]"
                    : "bg-white border-zinc-200"
            }`}
            data-testid={testid}
        >
            <div className="flex items-center justify-between">
                <span
                    className={`text-[10px] md:text-xs uppercase tracking-[0.18em] truncate ${
                        accent ? "text-white/70" : "text-zinc-500"
                    }`}
                >
                    {label}
                </span>
                <div
                    className={`w-7 h-7 md:w-8 md:h-8 rounded-sm flex items-center justify-center border shrink-0 ${
                        accent
                            ? "bg-white/15 border-white/20 text-white"
                            : "bg-zinc-100 border-zinc-200 text-zinc-700"
                    }`}
                >
                    <Icon className="w-3.5 h-3.5 md:w-4 md:h-4" />
                </div>
            </div>
            <div className="font-display text-2xl md:text-3xl font-black tracking-tighter leading-none break-words">
                {value}
            </div>
            {sub && (
                <div
                    className={`text-[11px] md:text-xs font-mono truncate ${
                        accent ? "text-white/70" : "text-zinc-500"
                    }`}
                >
                    {sub}
                </div>
            )}
        </div>
    );
}

function CostSparkline({ data }) {
    // simple inline svg sparkline of cost per day
    const max = Math.max(0.0001, ...data.map((d) => d.cost || 0));
    const width = 280;
    const height = 80;
    const step = data.length > 1 ? width / (data.length - 1) : 0;
    const points = data.map((d, i) => {
        const x = i * step;
        const y = height - ((d.cost || 0) / max) * (height - 8) - 4;
        return [x, y];
    });
    const path = points.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
    const area = `${path} L${width},${height} L0,${height} Z`;
    const total = data.reduce((s, d) => s + (d.cost || 0), 0);
    const calls = data.reduce((s, d) => s + (d.calls || 0), 0);
    return (
        <div>
            <div className="font-display text-3xl font-black tracking-tighter leading-none">
                {formatCost(total)}
            </div>
            <div className="text-xs font-mono text-zinc-500 mb-3">
                {calls} AI-aanvragen
            </div>
            <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-20">
                <path d={area} fill="#0044FF" fillOpacity="0.1" />
                <path d={path} fill="none" stroke="#0044FF" strokeWidth="2" />
                {points.map(([x, y], i) => (
                    <circle key={i} cx={x} cy={y} r="2" fill="#0044FF" />
                ))}
            </svg>
        </div>
    );
}

function EmptyHint({ text, link, linkLabel }) {
    return (
        <div className="border border-dashed border-zinc-300 rounded-md p-6 text-center text-sm text-zinc-500">
            {text}
            {link && (
                <div className="mt-3">
                    <Link to={link} className="text-[#0044FF] hover:underline font-medium">
                        {linkLabel} →
                    </Link>
                </div>
            )}
        </div>
    );
}
